/**
 * Use-case registry — the ONE place use cases are registered and validated.
 *
 * Central resolvers (project wizard, dashboard resolver, readiness resolver,
 * onboarding resolver, Command Center context) consume this registry; adding
 * a use case means registering a manifest + i18n + readiness/dashboard ids —
 * never forking core logic with scattered `if (useCase === …)` branches.
 *
 * Registration is fail-closed: duplicate ids, unknown capability references,
 * unknown binding purposes and unknown work profiles are refused with a
 * thrown error, so a broken manifest can never half-register.
 */

import { PROJECT_BINDING_PURPOSES } from "@/lib/project-bindings";
import { WORK_PROFILES } from "@/lib/navigation/work-profiles";
import { TEMPLATES_BY_ID } from "@/lib/governance/templates";
import { isProjectCapabilityId } from "./capabilities";
import {
  REMEDIATION_START_MODES,
  USE_CASE_AVAILABILITY,
  type UseCaseDefinition,
} from "./types";
import { supportUseCase } from "./manifests/support";
import { engineeringUseCase } from "./manifests/engineering";
import { deploymentUseCase } from "./manifests/deployment";
import { securityUseCase } from "./manifests/security";
import { knowledgeUseCase } from "./manifests/knowledge";
import { customUseCase } from "./manifests/custom";

const CONTEXT_MODES = ["read_only", "sandbox", "writable"] as const;
const ID_RE = /^[a-z][a-z0-9_-]*$/;

export class UseCaseRegistrationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UseCaseRegistrationError";
  }
}

function validateDefinition(def: UseCaseDefinition): void {
  const fail = (msg: string): never => {
    throw new UseCaseRegistrationError(`use case "${def.id}": ${msg}`);
  };
  if (!ID_RE.test(def.id)) fail("id must be lowercase kebab/snake");
  if (!Number.isInteger(def.version) || def.version < 1) fail("version must be >= 1");
  if (!USE_CASE_AVAILABILITY.includes(def.availability)) {
    fail(`unknown availability "${def.availability}"`);
  }
  for (const key of [
    def.labelKey, def.shortDescriptionKey, def.fullDescriptionKey, def.primaryOutcomeKey,
  ]) {
    if (!key || typeof key !== "string") fail("i18n keys must be non-empty");
  }
  for (const c of [...def.requiredCapabilities, ...def.recommendedCapabilities]) {
    if (!isProjectCapabilityId(c)) fail(`unknown capability reference "${c}"`);
  }
  for (const p of def.suggestedBindingPurposes) {
    if (!(PROJECT_BINDING_PURPOSES as readonly string[]).includes(p)) {
      fail(`unknown binding purpose "${p}"`);
    }
  }
  for (const w of def.applicableWorkProfiles) {
    if (!(WORK_PROFILES as readonly string[]).includes(w)) {
      fail(`unknown work profile "${w}"`);
    }
  }
  if (
    def.defaultExecutionMode !== undefined &&
    !(CONTEXT_MODES as readonly string[]).includes(def.defaultExecutionMode)
  ) {
    fail(`unknown execution mode "${def.defaultExecutionMode}"`);
  }
  if (
    def.defaultRemediationStartMode !== undefined &&
    !REMEDIATION_START_MODES.includes(def.defaultRemediationStartMode)
  ) {
    fail(`unknown remediation start mode "${def.defaultRemediationStartMode}"`);
  }
  if (
    def.defaultGovernanceProfileId !== undefined &&
    !TEMPLATES_BY_ID[def.defaultGovernanceProfileId]
  ) {
    fail(`unknown governance template "${def.defaultGovernanceProfileId}"`);
  }
  if (
    def.requiredPlanFeature !== undefined &&
    (!def.requiredPlanFeature || !/^[a-z][a-z0-9_-]*$/.test(def.requiredPlanFeature))
  ) {
    fail(`malformed plan feature "${def.requiredPlanFeature}"`);
  }
  for (const list of [def.onboardingStepIds, def.dashboardSectionIds, def.readinessCheckIds]) {
    for (const id of list) {
      if (!id || !/^[a-z][a-z0-9_.-]*$/.test(id)) fail(`malformed resolver id "${id}"`);
    }
  }
}

/** An isolated registry instance. Production uses the module-level singleton
 *  below; tests create their own to prove a new manifest registers without
 *  touching any central component. */
export type UseCaseRegistry = {
  register(def: UseCaseDefinition): void;
  byId(id: string): UseCaseDefinition | undefined;
  /** All registered use cases in registration order, hidden ones excluded
   *  unless asked for. */
  list(opts?: { includeHidden?: boolean }): UseCaseDefinition[];
  ids(): string[];
};

export function createUseCaseRegistry(): UseCaseRegistry {
  const byId = new Map<string, UseCaseDefinition>();
  return {
    register(def: UseCaseDefinition): void {
      validateDefinition(def);
      if (byId.has(def.id)) {
        throw new UseCaseRegistrationError(`duplicate use case id "${def.id}"`);
      }
      byId.set(def.id, Object.freeze({ ...def }));
    },
    byId: (id) => byId.get(id),
    list: (opts) =>
      [...byId.values()].filter(
        (d) => opts?.includeHidden || d.availability !== "hidden",
      ),
    ids: () => [...byId.keys()],
  };
}

/** The production registry with the six built-in use cases. HR is
 *  deliberately NOT registered — it does not exist as runtime functionality.
 *  Later use cases register here (manifest + i18n + resolver ids). */
export const USE_CASE_REGISTRY: UseCaseRegistry = createUseCaseRegistry();
for (const def of [
  supportUseCase,
  engineeringUseCase,
  deploymentUseCase,
  securityUseCase,
  knowledgeUseCase,
  customUseCase,
]) {
  USE_CASE_REGISTRY.register(def);
}

export function resolveUseCase(id: string): UseCaseDefinition | undefined {
  return USE_CASE_REGISTRY.byId(id);
}

export function listUseCases(opts?: { includeHidden?: boolean }): UseCaseDefinition[] {
  return USE_CASE_REGISTRY.list(opts);
}

export function isKnownUseCaseId(v: unknown): v is string {
  return typeof v === "string" && USE_CASE_REGISTRY.byId(v) !== undefined;
}
